import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FiUploadCloud,
  FiFileText,
  FiSearch,
  FiTrash2,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
  FiX,
  FiExternalLink,
  FiSave,
  FiPaperclip,
  FiRefreshCw,
  FiLink,
  FiInbox,
  FiPlus,
  FiImage,
  FiChevronRight,
} from "react-icons/fi";
import { useClient } from "../context/ClientContext";
import { receiptService } from "../services/receiptService";
import { billService } from "../services/billService";
import { vendorService } from "../services/vendorService";
import { findMatchingBills } from "../utils/receiptBillMatcher";
import { useNavigate } from "react-router-dom";

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
const labelClass = "mb-1.5 block text-xs font-semibold text-slate-600";
const formatMoney = (amount, currency = "USD") => {
  if (amount === null || amount === undefined || amount === "") return "—";
  const number = Number(amount);
  if (!Number.isFinite(number)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(number);
  } catch {
    return `${currency || "USD"} ${number.toFixed(2)}`;
  }
};
const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};
const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};
const emptyForm = () => ({
  vendor_id: "",
  bill_id: "",
  bill_payment_id: "",
  receipt_date: "",
  amount: "",
  currency: "USD",
  notes: "",
});
const normalizeName = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(
      /\b(incorporated|inc|llc|ltd|limited|corporation|corp|company|co)\b/g,
      " ",
    )
    .replace(/\s+/g, " ")
    .trim();
const exactName = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
const findVendorMatch = (name, vendors) => {
  if (!name) return null;
  const exact = vendors.find((v) =>
    [v.vendor_name, v.company_name].some(
      (candidate) => exactName(candidate) === exactName(name),
    ),
  );
  if (exact) return exact;
  const normalized = normalizeName(name);
  if (!normalized) return null;
  const matches = vendors.filter((v) =>
    [v.vendor_name, v.company_name].some(
      (candidate) => normalizeName(candidate) === normalized,
    ),
  );
  return matches.length === 1 ? matches[0] : null;
};
const formFromReceipt = (receipt) => ({
  vendor_id: receipt.vendor_id || "",
  bill_id: receipt.bill_id || "",
  bill_payment_id: receipt.bill_payment_id || "",
  receipt_date: receipt.receipt_date || "",
  amount: receipt.amount == null ? "" : String(receipt.amount),
  currency: receipt.currency || "USD",
  notes: receipt.notes || "",
});
const suggestedForm = (current, extracted, vendorMatch) => ({
  ...current,
  vendor_id: current.vendor_id || vendorMatch?.id || "",
  receipt_date: current.receipt_date || extracted.document_date || "",
  amount:
    current.amount !== ""
      ? current.amount
      : extracted.total_amount == null
        ? ""
        : String(extracted.total_amount),
  currency:
    current.currency && current.currency !== "USD"
      ? current.currency
      : extracted.currency || current.currency || "USD",
});

// Warnings are advisory: a bill can legitimately have several supporting documents.
const getLinkWarnings = (
  receipt,
  form,
  bill,
  vendorMap,
  otherReceipts = [],
) => {
  if (!bill || !receipt) return [];
  const warnings = [];
  const ai = receipt.extracted_data || {};
  const receiptVendorId = form.vendor_id || receipt.vendor_id;
  const detectedName = receipt.extracted_vendor_name || ai.vendor_name || "";
  const billVendor = vendorMap[bill.vendor_id];
  if (receiptVendorId && receiptVendorId !== bill.vendor_id) {
    warnings.push("The selected vendor differs from the bill vendor.");
  } else if (
    !receiptVendorId &&
    detectedName &&
    billVendor &&
    ![billVendor.vendor_name, billVendor.company_name].some(
      (name) => normalizeName(name) === normalizeName(detectedName),
    )
  ) {
    warnings.push("The AI-detected vendor name differs from the bill vendor.");
  }
  const amount =
    form.amount !== ""
      ? Number(form.amount)
      : ai.total_amount == null
        ? null
        : Number(ai.total_amount);
  if (
    amount !== null &&
    Number.isFinite(amount) &&
    bill.total != null &&
    Number.isFinite(Number(bill.total)) &&
    Math.abs(amount - Number(bill.total)) > 0.01
  ) {
    warnings.push(
      `Receipt amount ${formatMoney(amount, form.currency)} differs from bill total ${formatMoney(bill.total, bill.currency)}.`,
    );
  }
  if (
    form.currency &&
    bill.currency &&
    form.currency.toUpperCase() !== bill.currency.toUpperCase()
  ) {
    warnings.push(
      `Receipt currency ${form.currency.toUpperCase()} differs from bill currency ${bill.currency.toUpperCase()}.`,
    );
  }
  const linked = otherReceipts.filter(
    (item) => item.id !== receipt.id && item.bill_id === bill.id,
  );
  if (linked.length)
    warnings.push(
      `${linked.length} other receipt${linked.length === 1 ? " is" : "s are"} already linked to this bill. Verify this is not a duplicate document.`,
    );
  return warnings;
};

function StatCard({ title, value, icon: Icon, subtitle }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-slate-500">{title}</span>
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-50 text-slate-500">
          <Icon size={18} />
        </div>
      </div>
      <div className="mt-3 text-2xl font-bold tracking-tight text-slate-900">
        {value}
      </div>
      <p className="mt-1 text-xs text-slate-400">{subtitle}</p>
    </div>
  );
}
function ReceiptStatus({ receipt }) {
  const linked = Boolean(receipt.bill_id || receipt.bill_payment_id);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${linked ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}
    >
      {linked ? <FiCheckCircle size={12} /> : <FiAlertCircle size={12} />}{" "}
      {linked ? "Linked" : "Unlinked"}
    </span>
  );
}

export default function Receipts() {
  const navigate = useNavigate();
  const { activeClient } = useClient();
  const organizationId = activeClient?.id;
  const fileInputRef = useRef(null);
  const orgRef = useRef(organizationId);
  orgRef.current = organizationId;
  const selectedRef = useRef(null);
  const [receipts, setReceipts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [bills, setBills] = useState([]);
  const [billPayments, setBillPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openingFile, setOpeningFile] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [creatingVendor, setCreatingVendor] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCreateVendor, setShowCreateVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState("");
  const [linkConfirmed, setLinkConfirmed] = useState(false);
  const [serverLinkWarnings, setServerLinkWarnings] = useState([]);


  const loadData = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const [receiptRows, vendorRows, billRows] = await Promise.all([
        receiptService.getReceipts(organizationId),
        vendorService.getVendors(organizationId),
        billService.getBills(organizationId),
      ]);
      if (orgRef.current !== organizationId) return;
      setReceipts(receiptRows || []);
      setVendors(vendorRows || []);
      setBills(billRows || []);
    } catch (err) {
      if (orgRef.current === organizationId)
        setError(err.message || "Unable to load receipts.");
    } finally {
      if (orgRef.current === organizationId) setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    selectedRef.current = null;
    setReceipts([]);
    setVendors([]);
    setBills([]);
    setBillPayments([]);
    setSelectedReceipt(null);
    setForm(emptyForm());
    setError("");
    setSuccess("");
    setModalError("");
    setModalSuccess("");
    setShowCreateVendor(false);
    setShowDeleteConfirm(false);
    setLinkConfirmed(false);
    setServerLinkWarnings([]);
    if (organizationId) loadData();
  }, [organizationId, loadData]);

  const vendorMap = useMemo(
    () => Object.fromEntries(vendors.map((v) => [v.id, v])),
    [vendors],
  );
  const billMap = useMemo(
    () => Object.fromEntries(bills.map((b) => [b.id, b])),
    [bills],
  );
  const linkedCount = receipts.filter(
    (r) => r.bill_id || r.bill_payment_id,
  ).length;
  const filteredReceipts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return receipts.filter((r) => {
      const linked = Boolean(r.bill_id || r.bill_payment_id);
      const text = [
        r.file_name,
        vendorMap[r.vendor_id]?.vendor_name,
        r.extracted_vendor_name,
        r.document_number,
        billMap[r.bill_id]?.bill_number,
        r.notes,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return (
        (!query || text.includes(query)) &&
        (filter === "all" ||
          (filter === "linked" && linked) ||
          (filter === "unlinked" && !linked))
      );
    });
  }, [receipts, vendorMap, billMap, search, filter]);
  const selectedBill = useMemo(
    () => bills.find((b) => b.id === form.bill_id),
    [bills, form.bill_id],
  );
  const availableBills = useMemo(
    () =>
      form.vendor_id
        ? bills.filter((b) => b.vendor_id === form.vendor_id)
        : bills,
    [bills, form.vendor_id],
  );
  // Suggestions use current review values; they never create or modify bills.
  const suggestedBills = useMemo(() => {
    if (!selectedReceipt || !organizationId) return [];
    const ai = selectedReceipt.extracted_data || {};
    const reviewReceipt = {
      ...selectedReceipt,
      vendor_id: form.vendor_id || null,
      amount:
        form.amount !== "" ? Number(form.amount) : (ai.total_amount ?? null),
      currency: form.currency || ai.currency || "",
      document_number:
        selectedReceipt.document_number || ai.document_number || "",
    };
    return findMatchingBills({
      receipt: reviewReceipt,
      bills,
      vendors,
      organizationId,
    });
  }, [
    selectedReceipt,
    organizationId,
    form.vendor_id,
    form.amount,
    form.currency,
    bills,
    vendors,
  ]);

  const linkWarnings = useMemo(
    () =>
      getLinkWarnings(selectedReceipt, form, selectedBill, vendorMap, receipts),
    [selectedReceipt, form, selectedBill, vendorMap, receipts],
  );
  const displayedLinkWarnings = [
    ...new Set([...linkWarnings, ...serverLinkWarnings]),
  ];

  const extracted =
    selectedReceipt?.extracted_data &&
    typeof selectedReceipt.extracted_data === "object"
      ? selectedReceipt.extracted_data
      : {};
  const detectedVendor =
    selectedReceipt?.extracted_vendor_name || extracted.vendor_name || "";
  const vendorMatch = useMemo(
    () => findVendorMatch(detectedVendor, vendors),
    [detectedVendor, vendors],
  );
  const modalBusy = saving || deleting || extracting || creatingVendor;

  const openReceipt = async (receipt, org = organizationId) => {
    selectedRef.current = receipt.id;
    setSelectedReceipt(receipt);
    setForm(formFromReceipt(receipt));
    setModalError("");
    setModalSuccess("");
    setShowCreateVendor(false);
    setShowDeleteConfirm(false);
    setLinkConfirmed(false);
    setServerLinkWarnings([]);
    setBillPayments([]);
    if (receipt.bill_id) {
      try {
        const rows = await billService.getBillPayments(org, receipt.bill_id);
        if (orgRef.current === org && selectedRef.current === receipt.id)
          setBillPayments(rows || []);
      } catch (err) {
        if (orgRef.current === org && selectedRef.current === receipt.id)
          setModalError(err.message || "Unable to load bill payments.");
      }
    }
  };

  const handleExtractReceipt = async (
    receipt,
    org = organizationId,
    vendorRows = vendors,
  ) => {
    if (!receipt || !org) return;
    setLinkConfirmed(false);
    setServerLinkWarnings([]);
    setExtracting(true);
    setModalError("");
    setModalSuccess("");
    try {
      const result = await receiptService.extractReceipt(org, receipt.id);
      if (orgRef.current !== org || selectedRef.current !== receipt.id) return;
      const ai = result.extracted || {};
      const match = findVendorMatch(ai.vendor_name, vendorRows);
      const updated = await receiptService.getReceipt(org, receipt.id);
      if (orgRef.current !== org || selectedRef.current !== receipt.id) return;
      setForm((previous) => suggestedForm(previous, ai, match));
      setSelectedReceipt(updated);
      setReceipts((previous) =>
        previous.map((item) => (item.id === updated.id ? updated : item)),
      );
      setModalSuccess(
        "AI extraction complete. Review the suggested information and save your changes.",
      );
    } catch (err) {
      if (orgRef.current === org && selectedRef.current === receipt.id)
        setModalError(err.message || "Unable to extract document information.");
    } finally {
      if (orgRef.current === org) setExtracting(false);
    }
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length || !organizationId) return;
    const org = organizationId;
    setUploading(true);
    setError("");
    setSuccess("");
    let count = 0;
    let first = null;
    try {
      for (const file of files) {
        if (orgRef.current !== org) break;
        const row = await receiptService.uploadReceipt(org, file);
        first ||= row;
        count++;
      }
      if (orgRef.current !== org) return;
      await loadData();
      setSuccess(
        `${count} document${count === 1 ? "" : "s"} uploaded. ${count > 1 ? "AI will scan the first document; scan others from their details." : ""}`,
      );
      if (first) {
        await openReceipt(first, org);
        await handleExtractReceipt(first, org);
      }
    } catch (err) {
      if (orgRef.current === org) {
        setError(
          `${count} document${count === 1 ? "" : "s"} uploaded. ${err.message || "Upload failed."}`,
        );
        await loadData();
      }
    } finally {
      if (orgRef.current === org) setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const closeReceipt = () => {
    if (modalBusy) return;
    selectedRef.current = null;
    setSelectedReceipt(null);
    setBillPayments([]);
    setForm(emptyForm());
    setShowDeleteConfirm(false);
    setShowCreateVendor(false);
    setLinkConfirmed(false);
    setServerLinkWarnings([]);
    setModalError("");
    setModalSuccess("");
  };

  const handleFormChange = (field, value) => {
    setModalSuccess("");
    setLinkConfirmed(false);
    setServerLinkWarnings([]);
    setForm((previous) => {
      if (field === "vendor_id")
        return {
          ...previous,
          vendor_id: value,
          bill_id: "",
          bill_payment_id: "",
        };
      if (field === "bill_id") {
        const bill = bills.find((item) => item.id === value);
        return {
          ...previous,
          bill_id: value,
          bill_payment_id: "",
          vendor_id: previous.vendor_id || bill?.vendor_id || "",
          currency: previous.currency,
        };
      }
      return { ...previous, [field]: value };
    });
    if (field === "vendor_id" || field === "bill_id") setBillPayments([]);
  };

  const handleBillChange = async (billId) => {
    handleFormChange("bill_id", billId);
    if (!billId) return;
    const org = organizationId;
    const receiptId = selectedReceipt?.id;
    try {
      const rows = await billService.getBillPayments(org, billId);
      if (orgRef.current === org && selectedRef.current === receiptId)
        setBillPayments(rows || []);
    } catch (err) {
      if (orgRef.current === org && selectedRef.current === receiptId)
        setModalError(err.message || "Unable to load bill payments.");
    }
  };

  const handleCreateVendor = async () => {
    if (!selectedReceipt || !organizationId) return;
    const name = newVendorName.trim();
    if (!name) {
      setModalError("Enter a vendor name.");
      return;
    }
    const existing = findVendorMatch(name, vendors);
    if (existing) {
      setForm((previous) => ({
        ...previous,
        vendor_id: existing.id,
        bill_id: "",
        bill_payment_id: "",
      }));
      setShowCreateVendor(false);
      setModalSuccess(
        "Matched an existing vendor instead of creating a duplicate.",
      );
      return;
    }
    const org = organizationId;
    const receiptId = selectedReceipt.id;
    setLinkConfirmed(false);
    setServerLinkWarnings([]);
    setCreatingVendor(true);
    setModalError("");
    setModalSuccess("");
    try {
      // Refresh immediately before insertion to avoid duplicates from stale lists.
      const currentVendors = await vendorService.getVendors(org);
      const match = findVendorMatch(name, currentVendors || []);
      const vendor =
        match || (await vendorService.createVendor(org, { vendor_name: name }));
      if (orgRef.current !== org || selectedRef.current !== receiptId) return;
      setVendors((previous) =>
        [...previous.filter((v) => v.id !== vendor.id), vendor].sort((a, b) =>
          a.vendor_name.localeCompare(b.vendor_name),
        ),
      );
      setForm((previous) => ({
        ...previous,
        vendor_id: vendor.id,
        bill_id: "",
        bill_payment_id: "",
      }));
      setBillPayments([]);
      setShowCreateVendor(false);
      setModalSuccess(
        match
          ? "Existing vendor selected. Click Save Changes to link it to this receipt."
          : "Vendor created. Click Save Changes to link it to this receipt.",
      );
    } catch (err) {
      if (orgRef.current === org && selectedRef.current === receiptId)
        setModalError(err.message || "Unable to create vendor.");
    } finally {
      if (orgRef.current === org) setCreatingVendor(false);
    }
  };

  const handleSave = async () => {
    if (!selectedReceipt || !organizationId) return;
    const org = organizationId;
    const receiptId = selectedReceipt.id;
    setSaving(true);
    setModalError("");
    setModalSuccess("");
    try {
      if (
        form.amount !== "" &&
        (!Number.isFinite(Number(form.amount)) || Number(form.amount) < 0)
      )
        throw new Error("Enter a valid receipt amount.");
      const currency = form.currency.trim().toUpperCase();
      if (!/^[A-Z]{3}$/.test(currency))
        throw new Error("Currency must be a three-letter code such as USD.");
      if (form.bill_payment_id && !form.bill_id)
        throw new Error("Select a bill before linking a payment.");
      const bill = bills.find((item) => item.id === form.bill_id);
      if (form.bill_id && !bill)
        throw new Error("Selected bill was not found.");
      if (bill && form.vendor_id && bill.vendor_id !== form.vendor_id)
        throw new Error(
          "The selected bill belongs to a different vendor. Select the correct vendor or bill before saving.",
        );
      if (bill) {
        // Refresh to detect links added since the page loaded; only same-org records are considered.
        const latestReceipts = await receiptService.getReceipts(org);
        if (orgRef.current !== org || selectedRef.current !== receiptId) return;
        const currentWarnings = getLinkWarnings(
          selectedReceipt,
          form,
          bill,
          vendorMap,
          latestReceipts || [],
        );
        setReceipts(latestReceipts || []);
        setServerLinkWarnings(currentWarnings);
        const newWarnings = currentWarnings.filter(
          (message) => !displayedLinkWarnings.includes(message),
        );
        if (currentWarnings.length && (!linkConfirmed || newWarnings.length)) {
          if (newWarnings.length) setLinkConfirmed(false);
          throw new Error(
            "Review the bill-linking warnings, check the confirmation box, then click Save Changes again.",
          );
        }
      }
      const payload = {
        vendor_id: form.vendor_id || bill?.vendor_id || null,
        bill_id: form.bill_id || null,
        bill_payment_id: form.bill_payment_id || null,
        receipt_date: form.receipt_date || null,
        amount: form.amount === "" ? null : Number(form.amount),
        currency,
        notes: form.notes.trim(),
      };
      const updated = await receiptService.updateReceipt(
        org,
        receiptId,
        payload,
      );
      if (orgRef.current !== org || selectedRef.current !== receiptId) return;
      setReceipts((previous) =>
        previous.map((item) => (item.id === updated.id ? updated : item)),
      );
      setSelectedReceipt(updated);
      setForm(formFromReceipt(updated));
      setLinkConfirmed(false);
      setServerLinkWarnings([]);
      setModalSuccess("Receipt details saved successfully.");
    } catch (err) {
      if (orgRef.current === org && selectedRef.current === receiptId)
        setModalError(err.message || "Unable to save receipt.");
    } finally {
      if (orgRef.current === org) setSaving(false);
    }
  };

  const handleCreateDraftBill = () => {
    if (!selectedReceipt || !organizationId || modalBusy) return;

    if (selectedReceipt.bill_id || selectedReceipt.bill_payment_id) {
      setModalError(
        "This receipt is already linked to an accounting record. Review the existing link before creating another bill."
      );
      return;
    }

    if (selectedReceipt.extraction_status !== "completed") {
      setModalError(
        "Extract the document with AI before creating a draft bill."
      );
      return;
    }

    const ai = selectedReceipt.extracted_data || {};

    const vendorId =
      form.vendor_id ||
      findVendorMatch(
        selectedReceipt.extracted_vendor_name || ai.vendor_name,
        vendors
      )?.id ||
      "";

    const billNumber = String(
      selectedReceipt.document_number ||
      ai.document_number ||
      ""
    ).trim();

    const billDate =
      ai.document_date ||
      form.receipt_date ||
      selectedReceipt.receipt_date ||
      "";

    const dueDate = ai.due_date || billDate;

    navigate("/purchases/bills", {
      state: {
        aiBillDraft: {
          organizationId,
          receiptId: selectedReceipt.id,
          vendor_id: vendorId,
          bill_number: billNumber,
          bill_date: billDate,
          due_date: dueDate,
          currency: (
            form.currency ||
            ai.currency ||
            "USD"
          ).toUpperCase(),
          description:
            ai.description ||
            `Purchase document ${billNumber || selectedReceipt.file_name}`,
          line_items: Array.isArray(ai.line_items)
            ? ai.line_items.map(item => ({
                description: item.description || "",
                quantity: item.quantity ?? null,
                unit_price: item.unit_price ?? null,
                tax_rate: item.tax_rate ?? null,
                tax_amount: item.tax_amount ?? null,
                line_total: item.line_total ?? null,
              }))
            : [],  
          subtotal: ai.subtotal ?? null,
          tax_amount: ai.tax_amount ?? null,
          total_amount:
            form.amount !== ""
              ? Number(form.amount)
              : ai.total_amount ?? null,
          detected_vendor:
            selectedReceipt.extracted_vendor_name ||
            ai.vendor_name ||
            "",
        },
      },
    });
  }; 

  const handleViewFile = async (receipt) => {
    setOpeningFile(true);
    setModalError("");
    const newTab = window.open("", "_blank");
    try {
      const url = await receiptService.getSignedUrl(receipt.file_path);
      if (newTab) newTab.location.href = url;
      else window.location.href = url;
    } catch (err) {
      if (newTab) newTab.close();
      setModalError(err.message || "Unable to open the document.");
    } finally {
      setOpeningFile(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedReceipt || !organizationId) return;
    const org = organizationId;
    const receiptId = selectedReceipt.id;
    setDeleting(true);
    setModalError("");
    try {
      await receiptService.deleteReceipt(org, receiptId);
      if (orgRef.current !== org || selectedRef.current !== receiptId) return;
      setReceipts((previous) =>
        previous.filter((item) => item.id !== receiptId),
      );
      selectedRef.current = null;
      setSelectedReceipt(null);
      setShowDeleteConfirm(false);
      setSuccess("Receipt deleted successfully.");
    } catch (err) {
      if (orgRef.current === org && selectedRef.current === receiptId)
        setModalError(err.message || "Unable to delete receipt.");
    } finally {
      if (orgRef.current === org) setDeleting(false);
    }
  };

  if (!organizationId)
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <FiInbox size={36} className="mb-4 text-slate-300" />
        <h2 className="text-lg font-semibold text-slate-900">
          Select a business
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Choose a business to manage its receipts.
        </p>
      </div>
    );

  return (
    <div className="mx-auto max-w-7xl pb-24">
      <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Receipts
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Upload purchase documents, review AI suggestions, and link them to
            your accounting records.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadData}
            disabled={loading || uploading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {uploading ? (
              <FiLoader className="animate-spin" />
            ) : (
              <FiUploadCloud />
            )}
            {uploading ? "Uploading..." : "Upload Receipt"}
          </button>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        multiple
        onChange={handleUpload}
        className="hidden"
      />
      {error && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError("")}>
            <FiX />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
          <FiCheckCircle className="mt-0.5 shrink-0" />
          <span className="flex-1">{success}</span>
          <button type="button" onClick={() => setSuccess("")}>
            <FiX />
          </button>
        </div>
      )}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          title="Total Receipts"
          value={receipts.length}
          icon={FiFileText}
          subtitle="Uploaded documents"
        />
        <StatCard
          title="Linked Receipts"
          value={linkedCount}
          icon={FiLink}
          subtitle="Attached to bills or payments"
        />
        <StatCard
          title="Unlinked Receipts"
          value={receipts.length - linkedCount}
          icon={FiPaperclip}
          subtitle="Not yet linked to a record"
        />
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-semibold text-slate-900">Receipt Inbox</h2>
            <p className="mt-1 text-xs text-slate-500">
              Review and organize uploaded documents.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search receipts..."
                className={`${inputClass} pl-10 sm:w-64`}
              />
            </div>
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              className={inputClass}
            >
              <option value="all">All Receipts</option>
              <option value="linked">Linked</option>
              <option value="unlinked">Unlinked</option>
            </select>
          </div>
        </div>
        {loading ? (
          <div className="flex min-h-72 items-center justify-center gap-3 text-sm text-slate-500">
            <FiLoader className="animate-spin" />
            Loading receipts...
          </div>
        ) : filteredReceipts.length === 0 ? (
          <div className="flex min-h-80 flex-col items-center justify-center px-6 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-blue-500">
              <FiFileText size={28} />
            </div>
            <h3 className="text-base font-semibold text-slate-900">
              {receipts.length
                ? "No matching receipts"
                : "No receipts uploaded yet"}
            </h3>
            <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">
              {receipts.length
                ? "Try changing your search or filter."
                : "Upload a purchase document to review and organize it."}
            </p>
            {!receipts.length && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
              >
                <FiPlus />
                Upload First Receipt
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[850px] text-left">
              <thead className="bg-slate-50 text-xs font-semibold text-slate-500">
                <tr>
                  <th className="px-6 py-4">Receipt</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Vendor</th>
                  <th className="px-5 py-4">Linked Record</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4 text-right">Amount</th>
                  <th className="px-5 py-4" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredReceipts.map((receipt) => {
                  const vendor = vendorMap[receipt.vendor_id];
                  const bill = billMap[receipt.bill_id];
                  return (
                    <tr
                      key={receipt.id}
                      onClick={() => openReceipt(receipt)}
                      className="cursor-pointer transition hover:bg-slate-50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                            {receipt.file_type === "application/pdf" ? (
                              <FiFileText size={19} />
                            ) : (
                              <FiImage size={19} />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="max-w-[220px] truncate text-sm font-semibold text-slate-900">
                              {receipt.file_name}
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                              {formatFileSize(receipt.file_size)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">
                        {formatDate(receipt.receipt_date || receipt.created_at)}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-700">
                        {vendor?.vendor_name ||
                          receipt.extracted_vendor_name ||
                          "—"}
                        {!vendor && receipt.extracted_vendor_name && (
                          <span className="block text-xs text-slate-400">
                            AI detected · not linked
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">
                        {bill
                          ? `${bill.bill_number}${receipt.bill_payment_id ? " · Payment" : ""}`
                          : "—"}
                      </td>
                      <td className="px-5 py-4">
                        <ReceiptStatus receipt={receipt} />
                      </td>
                      <td className="px-5 py-4 text-right text-sm font-semibold text-slate-900">
                        {formatMoney(receipt.amount, receipt.currency)}
                      </td>
                      <td className="px-5 py-4 text-slate-400">
                        <FiChevronRight />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="border-t border-slate-100 px-6 py-4 text-xs text-slate-500">
              Showing {filteredReceipts.length} of {receipts.length} receipts
            </div>
          </div>
        )}
      </div>

      {selectedReceipt && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-sm sm:p-6"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeReceipt();
          }}
        >
          <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-slate-900">
                  Receipt Details
                </h2>
                <p className="mt-1 max-w-md truncate text-sm text-slate-500">
                  {selectedReceipt.file_name}
                </p>
              </div>
              <button
                type="button"
                onClick={closeReceipt}
                disabled={modalBusy}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 disabled:opacity-50"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
              {modalError && (
                <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                  <FiAlertCircle className="mt-0.5 shrink-0" />
                  <span className="flex-1">{modalError}</span>
                  <button type="button" onClick={() => setModalError("")}>
                    <FiX />
                  </button>
                </div>
              )}
              {modalSuccess && (
                <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                  <FiCheckCircle className="mt-0.5 shrink-0" />
                  <span className="flex-1">{modalSuccess}</span>
                  <button type="button" onClick={() => setModalSuccess("")}>
                    <FiX />
                  </button>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-blue-600">
                    <FiFileText size={21} />
                  </div>
                  <div className="min-w-0">
                    <p className="max-w-[280px] truncate text-sm font-semibold text-slate-900">
                      {selectedReceipt.file_name}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {formatFileSize(selectedReceipt.file_size)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleViewFile(selectedReceipt)}
                  disabled={openingFile}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  {openingFile ? (
                    <FiLoader className="animate-spin" />
                  ) : (
                    <FiExternalLink />
                  )}
                  View File
                </button>
              </div>
              <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <FiFileText className="text-blue-600" />
                      AI Document Extraction
                    </h3>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Read document details and review suggestions before
                      saving.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleExtractReceipt(selectedReceipt)}
                    disabled={modalBusy}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {extracting ? (
                      <FiLoader className="animate-spin" />
                    ) : (
                      <FiRefreshCw />
                    )}
                    {extracting
                      ? "Reading Document..."
                      : selectedReceipt.extraction_status === "completed"
                        ? "Extract Again"
                        : "Extract with AI"}
                  </button>
                </div>
                {extracting && (
                  <p className="mt-3 text-xs font-medium text-blue-700">
                    AI is reading your document. Please wait...
                  </p>
                )}
                {selectedReceipt.extraction_status === "completed" &&
                  !extracting && (
                    <p className="mt-3 flex items-center gap-2 text-xs font-medium text-emerald-700">
                      <FiCheckCircle />
                      Extraction completed · suggestions are not accounting
                      entries
                    </p>
                  )}
                {detectedVendor && (
                  <div className="mt-3 rounded-lg border border-blue-100 bg-white p-3 text-xs text-slate-600">
                    <p>
                      <span className="font-semibold text-slate-700">
                        Detected vendor:
                      </span>{" "}
                      {detectedVendor}
                    </p>
                    {!form.vendor_id && (
                      <div className="mt-2">
                        <p className="text-amber-700">
                          Vendor has not been linked to this receipt.
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {vendorMatch && (
                            <button
                              type="button"
                              disabled={modalBusy}
                              onClick={() =>
                                handleFormChange("vendor_id", vendorMatch.id)
                              }
                              className="rounded-lg border border-blue-200 px-3 py-1.5 font-semibold text-blue-700 hover:bg-blue-50"
                            >
                              Use matching vendor: {vendorMatch.vendor_name}
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={modalBusy}
                            onClick={() => {
                              setNewVendorName(detectedVendor);
                              setShowCreateVendor(true);
                              setModalError("");
                            }}
                            className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-1.5 font-semibold text-blue-700 hover:bg-blue-50"
                          >
                            <FiPlus />
                            Add Vendor
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {showCreateVendor && (
                  <div className="mt-3 rounded-xl border border-blue-200 bg-white p-3">
                    <h4 className="text-sm font-semibold text-slate-900">
                      Create Vendor
                    </h4>
                    <p className="mt-1 text-xs text-slate-500">
                      Confirm the name. The vendor will be added to this
                      business only. The receipt link is saved when you click
                      Save Changes.
                    </p>
                    <label className={`${labelClass} mt-3`}>Vendor name</label>
                    <input
                      className={inputClass}
                      value={newVendorName}
                      onChange={(event) => setNewVendorName(event.target.value)}
                      maxLength={200}
                    />
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={handleCreateVendor}
                        disabled={creatingVendor || !newVendorName.trim()}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                      >
                        {creatingVendor ? (
                          <FiLoader className="animate-spin" />
                        ) : (
                          <FiPlus />
                        )}
                        {creatingVendor
                          ? "Creating..."
                          : "Confirm & Create Vendor"}
                      </button>
                      <button
                        type="button"
                        disabled={creatingVendor}
                        onClick={() => setShowCreateVendor(false)}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {selectedReceipt.extraction_status === "completed" && (
                  <div className="mt-3 rounded-xl border border-blue-100 bg-white p-3">
                    <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Extracted document details
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
                      {[
                        [
                          "Document type",
                          selectedReceipt.document_type ||
                            extracted.document_type,
                        ],
                        [
                          "Document number",
                          selectedReceipt.document_number ||
                            extracted.document_number,
                        ],
                        [
                          "Document date",
                          extracted.document_date
                            ? formatDate(extracted.document_date)
                            : null,
                        ],
                        [
                          "Due date",
                          extracted.due_date
                            ? formatDate(extracted.due_date)
                            : null,
                        ],
                        [
                          "Subtotal",
                          extracted.subtotal == null
                            ? null
                            : formatMoney(
                                extracted.subtotal,
                                extracted.currency || form.currency,
                              ),
                        ],
                        [
                          "Tax",
                          extracted.tax_amount == null
                            ? null
                            : formatMoney(
                                extracted.tax_amount,
                                extracted.currency || form.currency,
                              ),
                        ],
                        [
                          "Total",
                          extracted.total_amount == null
                            ? null
                            : formatMoney(
                                extracted.total_amount,
                                extracted.currency || form.currency,
                              ),
                        ],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <p className="text-slate-400">{label}</p>
                          <p className="mt-1 break-words font-semibold text-slate-800">
                            {value || "—"}
                          </p>
                        </div>
                      ))}
                    </div>
                    {Array.isArray(extracted.line_items) &&
                      extracted.line_items.length > 0 && (
                        <div className="mt-5 border-t border-slate-100 pt-4">
                          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                              Extracted Line Items
                            </h4>

                            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                              {extracted.line_items.length}{" "}
                              {extracted.line_items.length === 1 ? "item" : "items"}
                            </span>
                          </div>

                          <div className="overflow-x-auto rounded-xl border border-slate-200">
                            <table className="w-full min-w-[650px] text-left text-xs">
                              <thead className="bg-slate-50 text-slate-500">
                                <tr>
                                  <th className="px-3 py-3 font-semibold">Description</th>
                                  <th className="px-3 py-3 text-right font-semibold">Qty</th>
                                  <th className="px-3 py-3 text-right font-semibold">
                                    Unit Price
                                  </th>
                                  <th className="px-3 py-3 text-right font-semibold">Tax %</th>
                                  <th className="px-3 py-3 text-right font-semibold">
                                    Line Total
                                  </th>
                                </tr>
                              </thead>

                              <tbody className="divide-y divide-slate-100">
                                {extracted.line_items.map((item, index) => (
                                  <tr key={index}>
                                    <td className="px-3 py-3 font-medium text-slate-800">
                                      {item.description || "Unidentified item"}
                                    </td>

                                    <td className="px-3 py-3 text-right text-slate-600">
                                      {item.quantity ?? "—"}
                                    </td>

                                    <td className="px-3 py-3 text-right text-slate-600">
                                      {item.unit_price == null
                                        ? "—"
                                        : formatMoney(
                                            item.unit_price,
                                            extracted.currency || form.currency
                                          )}
                                    </td>

                                    <td className="px-3 py-3 text-right text-slate-600">
                                      {item.tax_rate == null
                                        ? "—"
                                        : `${item.tax_rate}%`}
                                    </td>

                                    <td className="px-3 py-3 text-right font-semibold text-slate-800">
                                      {item.line_total == null
                                        ? "—"
                                        : formatMoney(
                                            item.line_total,
                                            extracted.currency || form.currency
                                          )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <p className="mt-3 text-xs leading-5 text-slate-500">
                            AI-extracted values are suggestions. Verify each item's quantity,
                            price, tax, and total against the original document before
                            creating a bill.
                          </p>
                        </div>
                      )}
                    {extracted.description && (
                      <p className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-600">
                        {extracted.description}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div>
                <h3 className="mb-4 text-sm font-semibold text-slate-900">
                  Receipt Information
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelClass}>Vendor</label>
                    <select
                      value={form.vendor_id}
                      onChange={(event) =>
                        handleFormChange("vendor_id", event.target.value)
                      }
                      className={inputClass}
                    >
                      <option value="">Select vendor (optional)</option>
                      {vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.vendor_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Receipt Date</label>
                    <input
                      type="date"
                      value={form.receipt_date}
                      onChange={(event) =>
                        handleFormChange("receipt_date", event.target.value)
                      }
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.amount}
                      onChange={(event) =>
                        handleFormChange("amount", event.target.value)
                      }
                      placeholder="0.00"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Currency</label>
                    <input
                      type="text"
                      maxLength={3}
                      value={form.currency}
                      onChange={(event) =>
                        handleFormChange(
                          "currency",
                          event.target.value.toUpperCase(),
                        )
                      }
                      className={inputClass}
                    />
                  </div>
                </div>
                <p className="mt-2 text-xs text-slate-400">
                  These are editable accounting details. Click Save Changes to
                  confirm them.
                </p>
              </div>
              {/* Suggested bills are separate from AI extraction and receipt editing. */}
              {suggestedBills.length > 0 && (
                <div className="border-t border-slate-100 pt-6">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <FiSearch className="text-blue-600" /> Suggested Matching
                    Bills
                  </h3>
                  <p className="mt-1 mb-4 text-xs leading-5 text-slate-500">
                    Compare the suggested bills with your document. A match
                    score is a heuristic, not verified confidence.
                  </p>
                  <div className="space-y-3">
                    {suggestedBills.map((match) => {
                      const bill = match.bill;
                      const selected = form.bill_id === bill.id;
                      return (
                        <div
                          key={bill.id}
                          className={`rounded-xl border p-4 ${selected ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white"}`}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-slate-900">
                                {bill.bill_number || "Unnumbered Bill"}
                              </p>
                              <p className="mt-1 text-xs text-slate-500">
                                {vendorMap[bill.vendor_id]?.vendor_name ||
                                  "Unknown vendor"}{" "}
                                · {formatDate(bill.bill_date)}
                              </p>
                            </div>
                            <span
                              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${match.confidence === "high" ? "bg-emerald-50 text-emerald-700" : match.confidence === "medium" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}
                            >
                              {match.score}/100 match score
                            </span>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                            <p className="text-sm font-semibold text-slate-900">
                              {formatMoney(bill.total, bill.currency)}
                            </p>
                            <button
                              type="button"
                              disabled={modalBusy}
                              onClick={() => handleBillChange(bill.id)}
                              className={`rounded-lg px-3 py-2 text-xs font-semibold disabled:opacity-50 ${selected ? "bg-blue-100 text-blue-700" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                            >
                              {selected ? "Selected" : "Review & Link"}
                            </button>
                          </div>
                          {match.warnings.length > 0 && (
                            <div className="mt-3 space-y-1 rounded-lg bg-amber-50 p-3">
                              {match.warnings.map((warning) => (
                                <p
                                  key={warning}
                                  className="flex items-center gap-2 text-xs text-amber-700"
                                >
                                  <FiAlertCircle /> {warning}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="mt-3 text-xs text-slate-500">
                    Review & Link only selects a bill. Click Save Changes to
                    confirm the receipt association.
                  </p>
                </div>
              )}
              <div className="border-t border-slate-100 pt-6">
                <h3 className="mb-1 text-sm font-semibold text-slate-900">
                  Link to Accounting Record
                </h3>
                <p className="mb-4 text-xs leading-5 text-slate-500">
                  Attach this document to an existing bill or bill payment. No
                  new accounting transaction is created.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>Vendor Bill</label>
                    <select
                      value={form.bill_id}
                      onChange={(event) => handleBillChange(event.target.value)}
                      className={inputClass}
                    >
                      <option value="">No linked bill</option>
                      {availableBills.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.bill_number} — {formatMoney(b.total, b.currency)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {form.bill_id && (
                    <div>
                      <label className={labelClass}>
                        Bill Payment (optional)
                      </label>
                      <select
                        value={form.bill_payment_id}
                        onChange={(event) =>
                          handleFormChange(
                            "bill_payment_id",
                            event.target.value,
                          )
                        }
                        className={inputClass}
                      >
                        <option value="">Link to bill only</option>
                        {billPayments.map((p) => (
                          <option key={p.id} value={p.id}>
                            {formatDate(p.payment_date)} —{" "}
                            {formatMoney(
                              p.amount,
                              selectedBill?.currency || form.currency,
                            )}
                            {p.reference_number
                              ? ` (${p.reference_number})`
                              : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
              {form.bill_id && displayedLinkWarnings.length > 0 && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                    <FiAlertCircle /> Review before linking
                  </h3>
                  <p className="mt-1 text-xs text-amber-800">
                    The selected bill has possible discrepancies. This does not
                    automatically mean the match is wrong.
                  </p>
                  <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-amber-900">
                    {displayedLinkWarnings.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                  <label className="mt-4 flex cursor-pointer items-start gap-2 text-xs font-medium text-amber-950">
                    <input
                      type="checkbox"
                      checked={linkConfirmed}
                      onChange={(event) =>
                        setLinkConfirmed(event.target.checked)
                      }
                      className="mt-0.5"
                    />
                    I reviewed these warnings and confirm that this receipt
                    should be linked to the selected bill.
                  </label>
                  <p className="mt-2 text-xs text-amber-800">
                    Saving links the document only; it does not create or modify
                    a bill or payment.
                  </p>
                </div>
              )}
              <div className="border-t border-slate-100 pt-6">
                <label className={labelClass}>Notes</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(event) =>
                    handleFormChange("notes", event.target.value)
                  }
                  placeholder="Add any additional details about this document..."
                  className={`${inputClass} resize-none`}
                />
              </div>
              {showDeleteConfirm && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                  <h4 className="text-sm font-semibold text-rose-800">
                    Delete this receipt?
                  </h4>
                  <p className="mt-1 text-xs leading-5 text-rose-700">
                    The document and receipt record will be removed. Linked
                    bills and payments will not be deleted.
                  </p>
                  <div className="mt-4 flex gap-2">
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={deleting}
                      className="rounded-lg bg-rose-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      {deleting ? "Deleting..." : "Confirm Delete"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={deleting}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-white px-6 py-4">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={modalBusy}
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
              >
                <FiTrash2 />
                Delete
              </button>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {selectedReceipt.extraction_status === "completed" &&
                  !selectedReceipt.bill_id &&
                  !selectedReceipt.bill_payment_id && (
                    <button
                      type="button"
                      onClick={handleCreateDraftBill}
                      disabled={modalBusy}
                      className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                    >
                      <FiPlus />
                      Create Draft Bill
                    </button>
                  )}

                <button
                  type="button"
                  onClick={closeReceipt}
                  disabled={modalBusy}
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={
                    modalBusy ||
                    (Boolean(form.bill_id) &&
                      displayedLinkWarnings.length > 0 &&
                      !linkConfirmed)
                  }
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? <FiLoader className="animate-spin" /> : <FiSave />}
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}